import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Copy, RefreshCw } from "lucide-react";
import { copyToClipboard } from "../../utils/clipboard";
import RegenerateSection from "./RegenerateSection";

function VideosTab({ content, activeVideo, setActiveVideo, onRegenerate, isRegenerating, onRegenerateItem, regeneratingItem }) {
  if (!content || content.length === 0) {
    return (
      <div className="text-center text-white/60 py-12">
        <p className="mb-4">No video content generated yet.</p>
        {onRegenerate && <RegenerateSection onRegenerate={onRegenerate} isRegenerating={isRegenerating} contentType="Videos" />}
      </div>
    );
  }

  const video = content[activeVideo];

  const aiPlatforms = [
    { name: "RUNWAY ML", color: "#a78bfa", key: "runway" },
    { name: "MIRAGE AI", color: "#00d4aa", key: "mirage" },
    { name: "PIKA LABS", color: "#f472b6", key: "pika" },
    { name: "HEYGEN", color: "#4ade80", key: "heygen" },
    { name: "MIDJOURNEY", color: "#60a5fa", key: "midjourney" }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
      {/* Video Selector */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {content.map((vid, index) => (
          <div key={index} className="flex items-center gap-1">
            <button
              onClick={() => setActiveVideo(index)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                index === activeVideo
                  ? "bg-[#00d4aa] text-black"
                  : "bg-white/10 text-white/60 hover:bg-white/20"
              }`}
            >
              Video {index + 1}
            </button>
            {onRegenerateItem && (
              <button
                onClick={() => onRegenerateItem(index)}
                disabled={regeneratingItem === index}
                className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                title={`Regenerate video ${index + 1}`}
              >
                <RefreshCw size={12} className={regeneratingItem === index ? 'animate-spin' : ''} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Video Details */}
      <Card className="card">
        <CardContent className="pt-6 space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">{video.title}</h2>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-white/10 rounded-full text-sm text-white">
                {video.type}
              </span>
              <span className="px-3 py-1 bg-white/10 rounded-full text-sm text-white">
                {video.duration}
              </span>
              {video.platforms.map((platform, i) => (
                <span key={i} className="px-3 py-1 bg-[#00d4aa]/20 text-[#00d4aa] rounded-full text-sm">
                  {platform}
                </span>
              ))}
            </div>
          </div>

          {/* Script Sections */}
          <div className="space-y-4">
            {/* Hook */}
            <div className="p-4 bg-red-500/10 border-l-4 border-red-500 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-xs uppercase text-red-400 font-semibold">
                  HOOK {video.hook.time}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(video.hook.script)}
                  className="text-red-400 hover:bg-red-500/10"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-white mb-2">{video.hook.script}</p>
              <div className="text-sm text-white/60 italic">
                Visual: {video.hook.visual}
              </div>
            </div>

            {/* Value */}
            <div className="p-4 bg-[#00d4aa]/10 border-l-4 border-[#00d4aa] rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-xs uppercase text-[#00d4aa] font-semibold">
                  VALUE {video.value.time}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(video.value.script)}
                  className="text-[#00d4aa] hover:bg-[#00d4aa]/10"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-white mb-2">{video.value.script}</p>
              <div className="text-sm text-white/60 italic">
                Visual: {video.value.visual}
              </div>
            </div>

            {/* CTA */}
            <div className="p-4 bg-orange-500/10 border-l-4 border-orange-500 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-xs uppercase text-orange-400 font-semibold">
                  CTA {video.cta.time}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(video.cta.script)}
                  className="text-orange-400 hover:bg-orange-500/10"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-white mb-2">{video.cta.script}</p>
              <div className="text-sm text-white/60 italic">
                Visual: {video.cta.visual}
              </div>
            </div>
          </div>

          {/* Production Notes */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-white/5 rounded-lg">
              <div className="font-mono text-xs uppercase text-white/40 mb-2">
                Thumbnail Concept
              </div>
              <p className="text-white text-sm">{video.thumbnail}</p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg">
              <div className="font-mono text-xs uppercase text-white/40 mb-2">
                Music Mood
              </div>
              <p className="text-white text-sm">{video.music}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Video Prompts */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4">AI Video Generation Prompts</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {aiPlatforms.map((platform) => (
            <Card key={platform.key} className="card">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div
                    className="font-mono text-xs font-bold uppercase"
                    style={{ color: platform.color }}
                  >
                    {platform.name}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(video.aiPrompts[platform.key])}
                    style={{ color: platform.color }}
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <p className="text-sm text-white/80">
                  {video.aiPrompts[platform.key]}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Regenerate Section */}
      {onRegenerate && (
        <RegenerateSection
          onRegenerate={() => onRegenerate('videos')}
          isRegenerating={isRegenerating}
          contentType="Videos"
        />
      )}
    </div>
  );
}

export default VideosTab;